/**
 * Food Log API - Daily food tracking with macro estimates
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3100';

/**
 * Get food log for a specific date
 */
export async function getFoodLog(date, token) {
  const response = await fetch(`${API_BASE}/api/food-log/${date}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch food log');
  }
  
  return response.json();
}

/**
 * Get food logs for a date range
 */
export async function getFoodLogRange(from, to, token) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  
  const response = await fetch(`${API_BASE}/api/food-log?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch food logs');
  }
  
  return response.json();
}

/**
 * Add a food entry
 */
export async function addFoodEntry(entry, token) {
  const response = await fetch(`${API_BASE}/api/food-log`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(entry)
  });
  
  if (!response.ok) {
    throw new Error('Failed to add food entry');
  }
  
  return response.json();
}

/**
 * Update a food entry
 */
export async function updateFoodEntry(id, updates, token) {
  const response = await fetch(`${API_BASE}/api/food-log/${id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    throw new Error('Failed to update food entry');
  }
  
  return response.json();
}

/**
 * Delete a food entry
 */
export async function deleteFoodEntry(id, token) {
  const response = await fetch(`${API_BASE}/api/food-log/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete food entry');
  }
  
  return response.json();
}

/**
 * Get macro tracking preferences
 */
export async function getMacroPreferences(token) {
  const response = await fetch(`${API_BASE}/api/food-log/preferences`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch macro preferences');
  }
  
  return response.json();
}

/**
 * Update macro tracking preferences
 */
export async function updateMacroPreferences(preferences, token) {
  const response = await fetch(`${API_BASE}/api/food-log/preferences`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(preferences)
  });
  
  if (!response.ok) {
    throw new Error('Failed to update macro preferences');
  }
  
  return response.json();
}

/**
 * Get today's date in YYYY-MM-DD format
 */
export function getToday() {
  return new Date().toISOString().split('T')[0];
}

/**
 * Format date for display
 */
export function formatDateDisplay(dateStr) {
  const date = new Date(dateStr);
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
}

/**
 * Default daily targets
 */
export const DEFAULT_TARGETS = {
  protein: 50,    // grams
  carbs: 250,     // grams
  fat: 65,        // grams
  calories: 2000, // kcal
  fiber: 25,      // grams
  sugar: 50,      // grams
  sodium: 2300,   // mg
  iron: 18,       // mg
  water: 2000     // ml
};

/**
 * Macro display config
 */
export const MACRO_CONFIG = {
  protein: { label: 'Protein', unit: 'g', color: '#EF4444' },
  carbs: { label: 'Carbs', unit: 'g', color: '#F59E0B' },
  fat: { label: 'Fat', unit: 'g', color: '#8B5CF6' },
  calories: { label: 'Calories', unit: 'kcal', color: '#10B981' },
  fiber: { label: 'Fiber', unit: 'g', color: '#6366F1' },
  sugar: { label: 'Sugar', unit: 'g', color: '#EC4899' },
  sodium: { label: 'Sodium', unit: 'mg', color: '#6B7280' },
  iron: { label: 'Iron', unit: 'mg', color: '#78716C' },
  water: { label: 'Water', unit: 'ml', color: '#0EA5E9' }
};
